import { kebabCase } from 'lodash';
import moment from 'moment';
import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { CompanyId, UserType } from 'api/models';
import { Field } from 'api/tasks';
import { PageLoading } from 'components/atoms/loading';
import { API_HOST } from 'config/environment';
import { useCurrentUserSideEffects } from 'contexts/providers';
import { RootState } from 'data';
import { saveFields } from 'data/fields';
import { useAccount } from 'hooks/use-account';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useFilterPointsFieldsFromTaskFields } from 'hooks/use-filter-point-fields-from-task-fields';
import { usePrerenderMembersAndTeamsFromIndexedDb } from 'hooks/use-idb';
import { useUser } from 'hooks/use-user';
import { useAllFieldsQuery, useTaskFieldsQuery } from 'queries';
import { useAccountDataQuery, useProjectsFromInitData } from 'queries/account';
import { ALL_FIELDS_QUERY_KEY } from 'queries/constants';
import { useMembersV2Query } from 'queries/members';
import { usePermissionSetDefinitionsForCtxMap } from 'queries/rbac';
import { ApiKey, logger } from 'services';
import { TOKEN_EXPIRY_KEY } from 'services/api-key';
import { putAll, refresh } from 'stores/fields';
import { getRGBFromHex } from 'utils/get-rgb-from-hex';
import { isNitroEnvironment } from 'utils/is-nitro-env';
import { rlNavigation } from 'utils/navigation';
import { addHopIdToSessionStorage } from 'utils/window-utils';
import { unregister } from 'vendor/cache-worker';

const LoadDependents = ({ onComplete }) => {
  const history = useHistory();

  useCurrentUserSideEffects();
  const dispatch = useDispatch();
  const companiesStore = useSelector(({ companies }: RootState) => companies);
  const usersStore = useSelector(({ users }: RootState) => users);
  const teamsStore = useSelector(({ teams }: RootState) => teams);

  const { filterPointsFieldsFromTaskFields } =
    useFilterPointsFieldsFromTaskFields();

  const [isSprintPlaningFeatureEnabled] = useAccountFeature(
    AccountFeature.SprintPlanning,
  );

  const membershipsStore = useSelector(
    ({ memberships }: RootState) => memberships,
  );
  const [fieldsLoaded, setFieldsLoaded] = useState(false);

  const { data: projectsData } = useProjectsFromInitData();

  const queryClient = useQueryClient();

  useAllFieldsQuery({
    onSuccess: () => {
      const allFields = queryClient.getQueryData(
        ALL_FIELDS_QUERY_KEY,
      ) as Field[];

      dispatch(putAll(allFields));
      setFieldsLoaded(true); // this is enough as dispacth is synchronous
    },
  });

  const { user: currentUser } = useUser();
  const {
    company: { companyId },
    userType,
    userId,
  } = currentUser;
  const { systemBotId } = useAccount();

  useEffect(() => {
    if (!fieldsLoaded) {
      return;
    }

    dispatch(
      refresh([
        usersStore,
        teamsStore,
        membershipsStore,
        companiesStore,
        CompanyId(companyId),
        currentUser,
        currentUser.userType === UserType.Native ? systemBotId : undefined,
        projectsData,
        filterPointsFieldsFromTaskFields,
        isSprintPlaningFeatureEnabled,
      ]),
    );
  }, [
    companiesStore,
    companyId,
    currentUser,
    dispatch,
    fieldsLoaded,
    membershipsStore,
    systemBotId,
    teamsStore,
    usersStore,
    projectsData,
    filterPointsFieldsFromTaskFields,
    isSprintPlaningFeatureEnabled,
  ]);

  useEffect(() => {
    if (userType === UserType.External) {
      window.setTimeout(() => onComplete());
    }
  }, [onComplete, userType]);

  // Hack way, to set history to rlNavigation
  // But to remove connected-react-router, we need to do this
  useEffect(() => {
    rlNavigation.set(history);
  }, [history]);

  useEffect(() => {
    const updateApiKey = async () => {
      try {
        const resp = await fetch(`${API_HOST}/api/v1/token-auth/jwt-auth`, {
          headers: {
            'Api-key': ApiKey.get()?.[1] ?? '',
          },
          credentials: 'same-origin',
        });
        const respData = await resp.json();

        if (respData?.newAuthExpiryTimestamp) {
          localStorage.setItem(
            TOKEN_EXPIRY_KEY,
            respData?.newAuthExpiryTimestamp,
          );
        }
      } catch (e: any) {
        logger.error('Error while fetching token', e);
      }
    };

    if (!isNitroEnvironment()) {
      return;
    }

    const authExpiryTS = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (ApiKey.get() !== null && authExpiryTS === null) {
      updateApiKey();
    }
  }, []);

  useEffect(() => {
    const refreshToken = async () => {
      try {
        const resp = await fetch(`${API_HOST}/api/v1/token-auth/refresh`, {
          credentials: 'same-origin',
        });
        const respData = await resp.json();

        if (respData?.newAuthExpiryTimestamp) {
          localStorage.setItem(
            TOKEN_EXPIRY_KEY,
            respData?.newAuthExpiryTimestamp,
          );
        }
      } catch (e: any) {
        logger.error('Error while refreshing token', e);
      }
    };

    if (!isNitroEnvironment()) {
      return;
    }

    const authExpiryLS = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (authExpiryLS !== null) {
      const expiryInt = parseInt(authExpiryLS, 10);

      if (expiryInt === -1 || currentUser.userType === UserType.External) {
        return;
      }
      const authExpiry = moment.unix(expiryInt);
      const now = moment();
      const diff = authExpiry.diff(now, 'days');

      if (diff < 30) {
        refreshToken();
      }
    }
  }, [currentUser.userType]);
  // Setting hopId for tabs unique per user
  useEffect(() => {
    addHopIdToSessionStorage(userId);
  }, [userId]);

  useEffect(() => {
    unregister(userId);
  }, [userId]);

  return null;
};

interface Props {
  children: any;
  isAccountDataFetched: boolean;
  onDependentsLoaded: (loading: boolean) => void;
  isLoading: boolean;
}

const RenderBootApp: FC<Props> = ({
  children,
  isAccountDataFetched,
  onDependentsLoaded,
}) => {
  return isAccountDataFetched ? (
    <>
      <LoadDependents onComplete={onDependentsLoaded} />
      {children}
    </>
  ) : (
    <PageLoading css />
  );
};

const stopRerenderPostLoading = (
  pre: Readonly<React.PropsWithChildren<Props>>,
): boolean => !!pre.isLoading;
const MuteBootUpdatesPostLoading = memo(RenderBootApp, stopRerenderPostLoading);

export const LazyBootApp = ({ children }) => {
  const [isLoading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const [dependentsLoaded, setDependentsLoaded] = useState(false);

  const { isSuccess: isAccountDataFetched, data: accountData } =
    useAccountDataQuery();

  const { isSuccess: isTaskFieldsFetched } = useTaskFieldsQuery({
    onSuccess: (result) => {
      dispatch(saveFields(result));
    },
  });
  const [membersDataLoadedIntoIndexedDB, setMembersDataLoadedIntoIndexedDB] =
    useState(false);

  const { isSuccess: isMembersV2Fetched } = useMembersV2Query({}, () => {
    setMembersDataLoadedIntoIndexedDB(true);
  });
  const { isSuccess: isPermissionSetDefinitionFetched } =
    usePermissionSetDefinitionsForCtxMap();

  usePrerenderMembersAndTeamsFromIndexedDb();

  useEffect(() => {
    if (
      isAccountDataFetched &&
      isTaskFieldsFetched &&
      dependentsLoaded &&
      isMembersV2Fetched &&
      membersDataLoadedIntoIndexedDB
    ) {
      requestAnimationFrame(() => setLoading(true));
    }
  }, [
    isAccountDataFetched,
    isTaskFieldsFetched,
    dependentsLoaded,
    isMembersV2Fetched,
    membersDataLoadedIntoIndexedDB,
  ]);

  const onDependentsLoaded = useCallback(() => {
    setDependentsLoaded(true);
  }, []);
  const [, setTimesheetsFeature] = useAccountFeature(AccountFeature.Timesheets);
  const [, setFinancialsFeature] = useAccountFeature(AccountFeature.Financials);
  const [, setSprintsFeature] = useAccountFeature(
    AccountFeature.SprintPlanning,
  );
  const [, setAiFillsFeature] = useAccountFeature(AccountFeature.AiFills);

  useLayoutEffect(() => {
    if (accountData) {
      const {
        account: { metaData },
        accountMeta: { features },
      } = accountData;

      if (metaData) {
        const { theme } = metaData;

        if (theme) {
          const root = document.documentElement;

          Object.keys(theme).forEach((variable) => {
            const variableValue = theme[variable];

            if (variableValue) {
              root.style.setProperty(`--${kebabCase(variable)}`, variableValue);
            }
          });
          if (theme.brandColorInRgb == null) {
            const themeBrandInNum = theme?.brandColor.slice(1);

            root.style.setProperty(
              `--${kebabCase('brandColorInRgb')}`,
              `${getRGBFromHex(themeBrandInNum)[0]}, ${
                getRGBFromHex(themeBrandInNum)[1]
              }, ${getRGBFromHex(themeBrandInNum)[2]}`,
            );
          }
        }
      }

      if (features?.[AccountFeature.Timesheets] != null) {
        setTimesheetsFeature(features[AccountFeature.Timesheets]);
      }
      if (features?.[AccountFeature.Financials] != null) {
        setFinancialsFeature(features[AccountFeature.Financials]);
      }
      if (features?.[AccountFeature.SprintPlanning] != null) {
        setSprintsFeature(features[AccountFeature.SprintPlanning]);
      }
      if (features?.[AccountFeature.AiFills] != null) {
        setAiFillsFeature(features[AccountFeature.AiFills]);
      }
    }
  }, [
    accountData,
    setFinancialsFeature,
    setTimesheetsFeature,
    setSprintsFeature,
    setAiFillsFeature,
  ]);

  // TODO: Remove this CurrentUserProvider once we have a better way to handle this
  // load routes only when account data is fetched and indexdb is populated
  return (
    <MuteBootUpdatesPostLoading
      isAccountDataFetched={
        !!(
          isAccountDataFetched &&
          membersDataLoadedIntoIndexedDB &&
          isPermissionSetDefinitionFetched
        )
      }
      onDependentsLoaded={onDependentsLoaded}
      isLoading={isLoading}
    >
      <>{children}</>
    </MuteBootUpdatesPostLoading>
  );
};
